/**
 * React Query keys and functions for playbooks data
 *
 * This file centralizes all query keys and API functions for React Query.
 * Using query key factories ensures consistent cache keys across the app.
 */

import type { Playbook } from '@/db/Types.ts'
import type { Drawing } from '@/types'
import type { PaginatedResponse } from '@/types/Pagination'
import { buildPaginationParams } from '@/api/utils/PaginationParams'
import { secureFetch, secureJsonFetch } from '@/utils/SecureFetch'

// ============================================================================
// TYPES
// ============================================================================

export interface PlayerData {
	x: number
	y: number
	label: string
	color?: string
}

export interface RawPreviewPlay {
	id: string
	custom_players: PlayerData[] | string | null
	custom_drawings: Drawing[] | string | null
	formation_id: string | null
	concept_drawings: Drawing[] | null
	formation_players: PlayerData[] | null
}

export interface PlaybookWithCount extends Playbook {
	play_count: number
	preview_plays?: RawPreviewPlay[]
}

export type PlaybookPage = PaginatedResponse<PlaybookWithCount> & {
	playbooks: PlaybookWithCount[]
}

// ============================================================================
// QUERY KEY FACTORIES
// ============================================================================

export const playbookKeys = {
	all: ['playbooks'] as const,
	lists: () => [...playbookKeys.all, 'list'] as const,
	list: (teamId: string | null, section?: string, folderId?: string | null) =>
		[...playbookKeys.lists(), teamId, section ?? 'all', folderId] as const,
	detail: (id: string) => ['playbooks', 'detail', id] as const,
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

async function handleResponse<T>(response: Response): Promise<T> {
	if (!response.ok) {
		if (response.status === 401) {
			throw new Error('UNAUTHORIZED')
		}
		const error = await response.text()
		throw new Error(error || `HTTP ${response.status}`)
	}
	return response.json()
}

export async function fetchPlaybooks(): Promise<PlaybookWithCount[]> {
	const response = await secureFetch('/api/playbooks')
	const data = await handleResponse<{ playbooks: PlaybookWithCount[] }>(response)
	return data.playbooks
}

export async function fetchPlaybooksPage(
	limit: number = 20,
	cursor?: string,
	section?: string,
	folderId?: string | null,
	teamId?: string | null,
): Promise<PlaybookPage> {
	const params = buildPaginationParams({ limit, cursor, section, folderId, teamId })

	const response = await secureFetch(`/api/playbooks?${params}`)
	const data = await handleResponse<PlaybookPage>(response)
	return data
}

export async function createPlaybook(data: {
	team_id: string | null
	name: string
	description?: string
}): Promise<Playbook> {
	const response = await secureJsonFetch('/api/playbooks', {
		method: 'POST',
		body: data,
	})
	const result = await handleResponse<{ playbook: Playbook }>(response)
	return result.playbook
}

export async function updatePlaybook(
	id: string,
	data: { name?: string; description?: string },
): Promise<Playbook> {
	const response = await secureJsonFetch(`/api/playbooks/${id}`, {
		method: 'PUT',
		body: data,
	})
	const result = await handleResponse<{ playbook: Playbook }>(response)
	return result.playbook
}

export async function deletePlaybook(id: string): Promise<void> {
	const response = await secureFetch(`/api/playbooks/${id}`, {
		method: 'DELETE',
	})
	if (!response.ok && response.status !== 401) {
		throw new Error(`Failed to delete playbook: ${response.status}`)
	}
	if (response.status === 401) {
		throw new Error('UNAUTHORIZED')
	}
}

export async function togglePlaybookStar(id: string): Promise<Playbook> {
	const response = await secureFetch(`/api/playbooks/${id}/star`, {
		method: 'PUT',
	})
	const result = await handleResponse<{ playbook: Playbook }>(response)
	return result.playbook
}

export async function restorePlaybook(id: string): Promise<Playbook> {
	const response = await secureFetch(`/api/playbooks/${id}/restore`, {
		method: 'PUT',
	})
	const result = await handleResponse<{ playbook: Playbook }>(response)
	return result.playbook
}

export async function permanentDeletePlaybook(id: string): Promise<void> {
	const response = await secureFetch(`/api/playbooks/${id}/permanent`, {
		method: 'DELETE',
	})
	if (!response.ok && response.status !== 401) {
		throw new Error(`Failed to permanently delete playbook: ${response.status}`)
	}
	if (response.status === 401) {
		throw new Error('UNAUTHORIZED')
	}
}

export async function emptyTrash(): Promise<number> {
	const response = await secureFetch('/api/trash', {
		method: 'DELETE',
	})
	const result = await handleResponse<{ deletedCount: number }>(response)
	return result.deletedCount
}
